import React, { useState, useEffect, useRef, useCallback } from 'react';
import { User, Briefcase, GraduationCap, Globe, Monitor, Car, Target, X, Save, Loader2, Trash2, ChevronLeft, PlusCircle } from 'lucide-react';
import { LITHUANIAN_CITIES, pinMajorCitiesFirst } from '../../constants/cities';
import FilterModal from '../Jobs/FilterModal';
import api from '../../services/api';
import axios from 'axios';
import { filtersToQueryParams } from '../../pages/Skelbimai';
import './CandidateSearchFilters.scss';

// Canonical importance order for the "Reikalavimai būsimam darbuotojui" modal so
// related options sit together (e.g. full-time next to part-time) instead of the
// arbitrary cv_templates order. Unknown values keep their original relative order
// at the end (Array.prototype.sort is stable).
const WORK_TYPE_ORDER = [
  'Pilnas etatas', 'Pusė etato', 'Pastovus', 'Pastovūs', 'Laikinas', 'Laikinas darbas',
  'Laikini', 'Sezoninis', 'Sezoninis darbas', 'Sezoniniai', 'Projektinis darbas', 'Projektiniai',
  'Praktika / stažuotė', 'Praktikos', 'Stažuotės', 'Profesinė praktika', 'Darbas namuose', 'Nuotolinis darbas',
];
const WORK_TIME_ORDER = [
  'Pilna darbo diena', 'Pusė darbo dienos', 'Nepilna darbo diena', 'Pamaininis darbas',
  'Lankstus grafikas', 'Laisvas darbo grafikas', 'Savaitgaliais', 'Vakarais', 'Naktinis darbas',
];
const orderByPriority = <T extends { value: string }>(options: T[], order: string[]): T[] => {
  const rank = (v: string) => { const i = order.indexOf(v); return i === -1 ? order.length : i; };
  return [...options].sort((a, b) => rank(a.value) - rank(b.value));
};

interface CVTemplate {
  id: number;
  category: string;
  value: string;
  parent_value?: string | null;
}

interface FilterOptions {
  searchKeyword: string;
  keywords: string[]; // Keyword chips
  location: string[];
  experience: string[];
  education: string[];
  languages: string[];
  computerSkills: string[];
  drivingLicense: string[];
  salary: string;
  workType: string[];
  age: string;
  gender: string[];
  status: string[];
  activity: string;
  cvLanguage: string[];
  hasPhoto: boolean;
  hasRecommendation: boolean;
  // Company-related filters
  companyNames: string[];
  lastWorkField: string[];
  companyActivity: string[];
  // Desired-job filters (from candidate CV's "Pageidaujamas darbas" section)
  desiredWorkField: string[];
  desiredPositionType: string[];
  desiredWorkTime: string[];
  desiredPosition: string[];
  desiredLocation: string[];
  // Freelance opt-in (CV has "I accept freelance gigs" checked)
  acceptsGigs: boolean;
  // Per-position minimum years of experience (key = position name).
  // Renders the "Patirtis nuo (metais)" input under each checked position.
  positionExperienceYears: Record<string, number>;
}

interface CandidateSearchFiltersProps {
  isVisible: boolean;
  onFiltersChange: (filters: FilterOptions) => void;
  onFiltersPreview?: (filters: FilterOptions) => void;
  onCollapse?: () => void;
  currentFilters: FilterOptions;
  liveCount?: number | null;
  countLoading?: boolean;
  hideSaveButton?: boolean;
}

const CandidateSearchFilters: React.FC<CandidateSearchFiltersProps> = ({
  isVisible,
  onFiltersChange,
  onFiltersPreview,
  onCollapse,
  currentFilters,
  liveCount,
  countLoading,
  hideSaveButton = false
}) => {
  const [openModal, setOpenModal] = useState<string | null>(null);
  const [facets, setFacets] = useState<Record<string, Record<string, number>>>({});
  const [facetsLoaded, setFacetsLoaded] = useState(false);
  const [facetsLoading, setFacetsLoading] = useState(false);
  const facetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [locationSearch, setLocationSearch] = useState('');
  const [workFieldSearch, setWorkFieldSearch] = useState('');
  const [positionSearch, setPositionSearch] = useState('');
  const [companySearch, setCompanySearch] = useState('');
  const [desiredPositionSearch, setDesiredPositionSearch] = useState('');
  const [desiredLocationSearch, setDesiredLocationSearch] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [searchTitle, setSearchTitle] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  
  // Experience modal tab state
  const [experienceTab, setExperienceTab] = useState<'workField' | 'lastWorkField' | 'companies'>('workField');
  
  // Company data from backend
  const [companies, setCompanies] = useState<{ companyName: string; count: number; activitySector?: string }[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  
  // Local state for modal filters - prevents re-renders during interaction
  const [localFilters, setLocalFilters] = useState<FilterOptions>(currentFilters);
  const [localAgeMin, setLocalAgeMin] = useState<number>(16);
  const [localAgeMax, setLocalAgeMax] = useState<number>(65);
  
  // CV Templates from admin
  const [cvTemplates, setCvTemplates] = useState<{
    education_level?: CVTemplate[];
    language?: CVTemplate[];
    cv_language?: CVTemplate[];
    work_field?: CVTemplate[];
    position?: CVTemplate[];
    position_type?: CVTemplate[];
    work_type?: CVTemplate[];
    work_time?: CVTemplate[];
    driving_category?: CVTemplate[];
    computer_skill_office?: CVTemplate[];
    computer_skill_accounting?: CVTemplate[];
    computer_skill_os?: CVTemplate[];
    computer_skill_programming?: CVTemplate[];
    computer_skill_database?: CVTemplate[];
    computer_skill_design?: CVTemplate[];
    city?: CVTemplate[];
  }>({});

  // Load CV templates on mount
  useEffect(() => {
    loadCvTemplates();
  }, []);

  // Only the dimensions a given modal actually renders are requested from the
  // facets endpoint. Each dimension is a full table scan server-side, so this
  // keeps each request to just what's on screen (fast) instead of all ~18.
  const MODAL_FACET_DIMS: Record<string, string[]> = {
    personalData: ['gender', 'cvLanguage', 'location', 'activity', 'hasPhoto', 'hasRecommendation', 'acceptsGigs'],
    experience: ['workFields', 'lastWorkField', 'positionType', 'position', 'companyActivity'],
    education: ['education'],
    languages: ['languages'],
    computerSkills: ['computerSkills'],
    drivingSkills: ['drivingLicense'],
    requirements: ['workType', 'desiredWorkField', 'desiredPositionType', 'desiredWorkTime', 'desiredPosition', 'desiredLocation'],
  };

  // Fetch facet counts for the given filter set + dimension list. We reuse the
  // exact same query-param builder as the search/count endpoints so the facet
  // WHERE clause matches the results WHERE clause (no stale/desynced counts).
  const fetchFacets = useCallback(async (filters: FilterOptions, dims: string[]) => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const token = localStorage.getItem('auth_token');
      const p = filtersToQueryParams(filters);
      if (dims.length > 0) p.set('dims', dims.join(','));
      const res = await fetch(`${API_URL}/cv/employer/facets?${p.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          // Counts from SUM(...) come back as strings via mysql2 (DECIMAL).
          // Coerce every facet count to a number so `=== 0` / `> 0` comparisons
          // behave (otherwise zero options ("0") never get disabled).
          const raw = (data.data || {}) as Record<string, Record<string, unknown>>;
          const normalized: Record<string, Record<string, number>> = {};
          Object.keys(raw).forEach((dim) => {
            const inner = raw[dim];
            if (inner && typeof inner === 'object') {
              const m: Record<string, number> = {};
              Object.keys(inner).forEach((k) => { m[k] = Number(inner[k]) || 0; });
              normalized[dim] = m;
            }
          });
          setFacets(normalized);
          setFacetsLoaded(true);
        }
      }
    } catch { /* non-blocking */ } finally {
      setFacetsLoading(false);
    }
  }, []);

  // The filter set that should drive facet counts — the in-progress local
  // selections (merged with the age slider) so options narrow live as the user
  // clicks, matching the employee-side behaviour.
  const effectiveFacetFilters: FilterOptions = {
    ...localFilters,
    age: (localAgeMin !== 16 || localAgeMax !== 65) ? `${localAgeMin}-${localAgeMax}` : '',
  };
  const effectiveFacetKey = JSON.stringify(effectiveFacetFilters);

  useEffect(() => {
    // Counts are only shown inside a modal — skip the (expensive) request entirely
    // when none is open.
    if (!openModal) { setFacetsLoading(false); return; }
    let dims = MODAL_FACET_DIMS[openModal] || [];
    // The "Darbo patirtis" modal has 3 tabs, each a separate full scan of
    // work_experience. Only fetch what the visible tab renders so the request
    // stays small (≈1–3 scans instead of 5).
    if (openModal === 'experience') {
      if (experienceTab === 'workField') dims = ['workFields', 'positionType', 'position'];
      else if (experienceTab === 'lastWorkField') dims = ['lastWorkField', 'positionType', 'position'];
      else dims = ['companyActivity', 'companyNames'];
    }
    // Flip the loading flag synchronously so options aren't briefly greyed out
    // by stale zero counts while the new request is in flight.
    setFacetsLoading(true);
    if (facetTimerRef.current) clearTimeout(facetTimerRef.current);
    facetTimerRef.current = setTimeout(() => fetchFacets(effectiveFacetFilters, dims), 300);
    return () => { if (facetTimerRef.current) clearTimeout(facetTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveFacetKey, openModal, experienceTab, fetchFacets]);

  // Counts are trustworthy for disabling options only once a request has
  // completed AND none is in flight (otherwise we'd grey out options against
  // stale zero counts mid-load).
  const facetsReady = facetsLoaded && !facetsLoading;

  // Sync local age state with currentFilters when filters change externally
  useEffect(() => {
    if (currentFilters.age) {
      const [min, max] = currentFilters.age.split('-').map(Number);
      if (!isNaN(min)) setLocalAgeMin(min);
      if (!isNaN(max)) setLocalAgeMax(max);
    } else {
      setLocalAgeMin(16);
      setLocalAgeMax(65);
    }
  }, [currentFilters.age]);

  const loadCvTemplates = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/cv-templates?grouped=true`);
      if (response.data && response.data.data) {
        setCvTemplates(response.data.data);
      }
    } catch (error) {
      console.error('Error loading CV templates:', error);
    }
  };

  // Load company data for the experience filter
  const loadCompanyData = async () => {
    if (companies.length > 0) return; // Already loaded
    
    setLoadingCompanies(true);
    try {
      // Use approved companies endpoint (admin-approved only). Work-field /
      // last-work-field counts now come from the contextual /cv/employer/facets
      // endpoint (see `facets.workFields` / `facets.lastWorkField`), so they
      // respect the active filters instead of showing global totals.
      const companiesRes = await axios.get(`${process.env.REACT_APP_API_URL}/companies/approved`);
      if (companiesRes.data?.data) setCompanies(companiesRes.data.data);
    } catch (error) {
      console.error('Error loading company data:', error);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const openFilterModal = (modalName: string) => {
    // Sync local filters with current filters when opening modal
    setLocalFilters(currentFilters);
    // Sync age slider
    if (currentFilters.age) {
      const [min, max] = currentFilters.age.split('-').map(Number);
      if (!isNaN(min)) setLocalAgeMin(min);
      if (!isNaN(max)) setLocalAgeMax(max);
    } else {
      setLocalAgeMin(16);
      setLocalAgeMax(65);
    }
    
    // Load company data when opening experience modal
    if (modalName === 'experience') {
      loadCompanyData();
      setExperienceTab('workField'); // Reset to first tab
    }
    
    setOpenModal(modalName);
  };

  const closeModal = () => {
    // Apply all local filter changes to parent when closing modal
    // Only set age if it's different from the default range (16-65)
    const isAgeChanged = localAgeMin !== 16 || localAgeMax !== 65;
    const finalFilters = {
      ...localFilters,
      age: isAgeChanged ? `${localAgeMin}-${localAgeMax}` : ''
    };
    onFiltersChange(finalFilters);
    setOpenModal(null);
  };

  // Trigger preview count when local filters change in modal
  const triggerPreview = (newLocalFilters: FilterOptions, ageMin: number = localAgeMin, ageMax: number = localAgeMax) => {
    if (onFiltersPreview) {
      // Only include age if it's different from default range
      const isAgeChanged = ageMin !== 16 || ageMax !== 65;
      const previewFilters = {
        ...newLocalFilters,
        age: isAgeChanged ? `${ageMin}-${ageMax}` : ''
      };
      onFiltersPreview(previewFilters);
    }
  };

  // Local handlers for modal - update local state and trigger preview
  const handleLocalArrayCheckbox = (field: keyof FilterOptions, value: string) => {
    const currentValues = localFilters[field] as string[];
    const wasChecked = currentValues.includes(value);
    const newValues = wasChecked
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];

    const newFilters: FilterOptions = {
      ...localFilters,
      [field]: newValues,
    };

    // Unchecking a position should also drop any per-position year minimum
    // tied to it — otherwise stale entries would resurface if the user
    // re-checks the same position later.
    if (wasChecked && field === 'experience' && localFilters.positionExperienceYears?.[value] !== undefined) {
      const next = { ...(localFilters.positionExperienceYears || {}) };
      delete next[value];
      newFilters.positionExperienceYears = next;
    }

    setLocalFilters(newFilters);
    triggerPreview(newFilters);
  };

  /**
   * Update the "Patirtis nuo (metais)" minimum tied to a single position.
   * `years <= 0` (or empty input) removes the entry entirely.
   */
  const handleLocalPositionYears = (position: string, years: number | null) => {
    const next = { ...(localFilters.positionExperienceYears || {}) };
    if (years === null || !Number.isFinite(years) || years <= 0) {
      delete next[position];
    } else {
      next[position] = years;
    }
    const newFilters: FilterOptions = {
      ...localFilters,
      positionExperienceYears: next,
    };
    setLocalFilters(newFilters);
    triggerPreview(newFilters);
  };

  /**
   * Render a single "position" row inside the experience modal:
   *   ▢ Pareigybė
   *      [ Patirtis nuo (metais) ]   ← only shown when the row is checked
   *
   * Reused by both `workField` and `lastWorkField` tabs so the per-position
   * year minimum behaves identically wherever a position can be selected.
   */
  const renderPositionRow = (pos: CVTemplate) => {
    const isChecked = localFilters.experience.includes(pos.value);
    const yearsValue = localFilters.positionExperienceYears?.[pos.value];
    const count = facets.position?.[pos.value] ?? 0;
    const disabled = facetsReady && count === 0 && !isChecked;
    return (
      <div key={pos.id} className="position-row">
        <label className="checkbox-item" style={{ opacity: disabled ? 0.4 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
          <input
            type="checkbox"
            checked={isChecked}
            onChange={() => handleLocalArrayCheckbox('experience', pos.value)}
          />
          <span>{pos.value}{facetsReady && count > 0 && <span className="filter-option-count"> ({count})</span>}</span>
        </label>
        {isChecked && (
          <input
            type="number"
            min={0}
            max={50}
            step={1}
            value={yearsValue ?? ''}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === '') {
                handleLocalPositionYears(pos.value, null);
                return;
              }
              const n = parseInt(raw, 10);
              handleLocalPositionYears(pos.value, Number.isFinite(n) ? n : null);
            }}
            className="position-years-input"
            placeholder="Patirtis nuo (metais)"
            aria-label={`${pos.value} – minimali patirtis metais`}
          />
        )}
      </div>
    );
  };

  const handleLocalInputChange = (field: keyof FilterOptions, value: string) => {
    const newFilters = {
      ...localFilters,
      [field]: value
    };
    setLocalFilters(newFilters);
    triggerPreview(newFilters);
  };

  const handleLocalBooleanChange = (field: keyof FilterOptions, value: boolean) => {
    const newFilters = {
      ...localFilters,
      [field]: value
    };
    setLocalFilters(newFilters);
    triggerPreview(newFilters);
  };

  // Original handlers for non-modal interactions (immediate parent update)
  const handleArrayCheckbox = (field: keyof FilterOptions, value: string) => {
    const currentValues = currentFilters[field] as string[];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    
    onFiltersChange({
      ...currentFilters,
      [field]: newValues
    });
  };

  const handleInputChange = (field: keyof FilterOptions, value: string) => {
    onFiltersChange({
      ...currentFilters,
      [field]: value
    });
  };

  const handleBooleanChange = (field: keyof FilterOptions, value: boolean) => {
    onFiltersChange({
      ...currentFilters,
      [field]: value
    });
  };

  const clearAllFilters = () => {
    // Clear localStorage when clearing all filters
    localStorage.removeItem('skelbimai_filters');
    
    onFiltersChange({
      searchKeyword: '',
      keywords: [],
      location: [],
      experience: [],
      education: [],
      languages: [],
      computerSkills: [],
      drivingLicense: [],
      salary: '',
      workType: [],
      age: '',
      gender: [],
      status: [],
      activity: '',
      cvLanguage: [],
      hasPhoto: false,
      hasRecommendation: false,
      companyNames: [],
      lastWorkField: [],
      companyActivity: [],
      desiredWorkField: [],
      desiredPositionType: [],
      desiredWorkTime: [],
      desiredPosition: [],
      desiredLocation: [],
      acceptsGigs: false,
      positionExperienceYears: {},
    });
  };

  const handleSaveSearch = () => {
    setShowSaveModal(true);
    setSaveMessage(null);
  };

  const confirmSaveSearch = async () => {
    if (!searchTitle.trim()) {
      setSaveMessage({ type: 'error', text: 'Prašome įvesti paieškos pavadinimą' });
      return;
    }

    setIsSaving(true);
    setSaveMessage(null);

    try {
      // Combine searchKeyword and keywords chips
      const allKeywords = [...currentFilters.keywords];
      if (currentFilters.searchKeyword && currentFilters.searchKeyword.trim()) {
        allKeywords.push(currentFilters.searchKeyword.trim());
      }

      // Prepare search data matching backend model
      const searchData = {
        title: searchTitle.trim(),
        location: currentFilters.location.length > 0 ? currentFilters.location : undefined,
        positions: allKeywords.length > 0 ? allKeywords : undefined,
        education_level: currentFilters.education.length > 0 ? currentFilters.education : undefined,
        languages: currentFilters.languages.length > 0 ? currentFilters.languages : undefined,
        technical_skills: currentFilters.computerSkills.length > 0 ? currentFilters.computerSkills : undefined,
        desired_work_type: currentFilters.workType.length > 0 ? currentFilters.workType : undefined,
        desired_salary: currentFilters.salary || undefined,
        experience_years: currentFilters.experience.length > 0 ? currentFilters.experience.join(', ') : undefined,
        is_subscribed: true // Set to true so it appears in "Išsaugotos paieškos" tab
      };

      const response = await api.post('/employer-saved-searches', searchData);

      if (response.data.success) {
        setSaveMessage({ type: 'success', text: 'Paieška sėkmingai išsaugota!' });
        setTimeout(() => {
          setShowSaveModal(false);
          setSearchTitle('');
          setSaveMessage(null);
        }, 1500);
      } else {
        setSaveMessage({ type: 'error', text: 'Nepavyko išsaugoti paieškos' });
      }
    } catch (error: any) {
      console.error('Save search error:', error);
      setSaveMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Klaida išsaugant paiešką' 
      });
    } finally {
      setIsSaving(false);
    }
  };

  const cancelSaveSearch = () => {
    setShowSaveModal(false);
    setSearchTitle('');
    setSaveMessage(null);
  };

  const removeFilter = (field: keyof FilterOptions, value?: string) => {
    if (field === 'hasPhoto' || field === 'hasRecommendation' || field === 'acceptsGigs') {
      handleBooleanChange(field, false);
    } else if (value && Array.isArray(currentFilters[field])) {
      handleArrayCheckbox(field, value);
    } else {
      handleInputChange(field, '');
    }
  };

  // Get active filters for display
  const getActiveFilters = () => {
    const active: { field: keyof FilterOptions; value: string; label: string }[] = [];
    
    // Add keywords as chips
    currentFilters.keywords.forEach(keyword => {
      active.push({ field: 'keywords', value: keyword, label: keyword });
    });
    
    currentFilters.location.forEach(loc => {
      active.push({ field: 'location', value: loc, label: loc });
    });
    
    currentFilters.experience.forEach(exp => {
      active.push({ field: 'experience', value: exp, label: exp });
    });
    
    currentFilters.education.forEach(edu => {
      active.push({ field: 'education', value: edu, label: edu });
    });
    
    currentFilters.languages.forEach(lang => {
      active.push({ field: 'languages', value: lang, label: lang });
    });
    
    currentFilters.computerSkills.forEach(skill => {
      active.push({ field: 'computerSkills', value: skill, label: skill });
    });
    
    currentFilters.drivingLicense.forEach(license => {
      active.push({ field: 'drivingLicense', value: license, label: `Vairavimas: ${license}` });
    });
    
    currentFilters.workType.forEach(type => {
      active.push({ field: 'workType', value: type, label: type });
    });
    
    // Only show age as active filter if it's not the default range (16-65)
    if (currentFilters.age && currentFilters.age !== '16-65') {
      active.push({ field: 'age', value: currentFilters.age, label: `Amžius: ${currentFilters.age}` });
    }
    
    if (currentFilters.salary) {
      active.push({ field: 'salary', value: currentFilters.salary, label: `Atlyginimas nuo: ${currentFilters.salary} EUR` });
    }

    // New personal data filters
    currentFilters.gender?.forEach(g => {
      active.push({ field: 'gender', value: g, label: g });
    });

    currentFilters.status?.forEach(s => {
      active.push({ field: 'status', value: s, label: s === 'currently_employed' ? 'Šiuo metu dirbantis' : s });
    });

    if (currentFilters.activity) {
      active.push({ field: 'activity', value: currentFilters.activity, label: `Aktyvumas: ${currentFilters.activity}` });
    }

    currentFilters.cvLanguage?.forEach(lang => {
      active.push({ field: 'cvLanguage', value: lang, label: `CV: ${lang}` });
    });

    if (currentFilters.hasPhoto) {
      active.push({ field: 'hasPhoto', value: 'true', label: 'CV su nuotrauka' });
    }

    if (currentFilters.hasRecommendation) {
      active.push({ field: 'hasRecommendation', value: 'true', label: 'CV su rekomendacija' });
    }

    // Company-related filters
    currentFilters.companyNames?.forEach(company => {
      active.push({ field: 'companyNames', value: company, label: `Įmonė: ${company}` });
    });

    currentFilters.lastWorkField?.forEach(field => {
      active.push({ field: 'lastWorkField', value: field, label: `Pask. darbas: ${field}` });
    });

    currentFilters.companyActivity?.forEach(activity => {
      active.push({ field: 'companyActivity', value: activity, label: `Veikla: ${activity}` });
    });

    currentFilters.desiredWorkField?.forEach(f => {
      active.push({ field: 'desiredWorkField', value: f, label: `Darbo sritis: ${f}` });
    });

    currentFilters.desiredPositionType?.forEach(t => {
      active.push({ field: 'desiredPositionType', value: t, label: `Pareigybių tipas: ${t}` });
    });

    currentFilters.desiredWorkTime?.forEach(t => {
      active.push({ field: 'desiredWorkTime', value: t, label: `Darbo laikas: ${t}` });
    });

    currentFilters.desiredPosition?.forEach(pos => {
      active.push({ field: 'desiredPosition', value: pos, label: `Pareigos: ${pos}` });
    });

    currentFilters.desiredLocation?.forEach(loc => {
      active.push({ field: 'desiredLocation', value: loc, label: `Darbo vieta: ${loc}` });
    });

    if (currentFilters.acceptsGigs) {
      active.push({ field: 'acceptsGigs', value: 'true', label: 'Priima Freelance pasiūlymus' });
    }

    return active;
  };

  const activeFilters = getActiveFilters();

  if (!isVisible) return null;

  return (
    <div className="candidate-search-filters">

      <div className="filters-content">
        <div className="filters-header">
          <h3>PASIRINKTI KRITERIJAI</h3>
          <div className="filters-header-actions">
            <button className="clear-filters-icon" onClick={clearAllFilters} title="Išvalyti filtrus">
              <Trash2 size={18} />
            </button>
            {onCollapse && (
              <button className="collapse-filters-btn" onClick={onCollapse} title="Slėpti filtrus">
                <ChevronLeft size={18} />
              </button>
            )}
          </div>
        </div>

        {/* Selected Filters - Grouped by section */}
        {activeFilters.length > 0 && (
          <div className="selected-filters">
            {(() => {
              const fieldToModal: Record<string, string> = {
                location: 'personalData', gender: 'personalData', status: 'personalData',
                activity: 'personalData', cvLanguage: 'personalData', age: 'personalData',
                experience: 'experience', lastWorkField: 'experience',
                companyNames: 'experience', companyActivity: 'experience',
                education: 'education', languages: 'languages',
                computerSkills: 'computerSkills', drivingLicense: 'drivingSkills',
                workType: 'requirements', salary: 'requirements',
                desiredWorkField: 'requirements', desiredPositionType: 'requirements',
                desiredWorkTime: 'requirements',
                desiredPosition: 'requirements', desiredLocation: 'requirements',
                acceptsGigs: 'personalData',
                keywords: 'personalData',
              };
              const groups: Record<string, typeof activeFilters> = {};
              activeFilters.forEach(f => {
                const modal = fieldToModal[f.field] || 'personalData';
                if (!groups[modal]) groups[modal] = [];
                groups[modal].push(f);
              });
              // Friendly category labels — shown above each chip group as a
              // tiny "sufleris" so the user can tell at a glance which filter
              // section the chip belongs to.
              const modalToLabel: Record<string, string> = {
                personalData: 'Asmeniniai duomenys',
                experience: 'Darbo patirtis',
                education: 'Išsilavinimas',
                languages: 'Kalbų įgūdžiai',
                computerSkills: 'Kompiuteriniai įgūdžiai',
                drivingSkills: 'Vairavimo įgūdžiai',
                requirements: 'Reikalavimai būsimam darbui',
              };
              return Object.entries(groups).map(([modal, filters]) => (
                <div key={modal} className="filter-group">
                  <div className="filter-group-content">
                    <span className="filter-group-label">{modalToLabel[modal] || modal}</span>
                    <div className="filter-group-pills">
                      {filters.map((filter, index) => (
                        <div key={index} className="filter-tag">
                          <span>{filter.label}</span>
                          <button onClick={() => removeFilter(filter.field, filter.value)}>
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                  <button className="filter-group-add" onClick={() => openFilterModal(modal)} title="Pridėti filtrą">
                    <PlusCircle size={22} />
                  </button>
                </div>
              ));
            })()}
          </div>
        )}

        {/* Save Search Button */}
        {!hideSaveButton && activeFilters.length > 0 && (
          <button className="save-search-btn" onClick={handleSaveSearch}>
            <Save size={16} />
            <span>Išsaugoti paiešką</span>
          </button>
        )}

        {/* Personal Data */}
        {(() => {
          const count = (currentFilters.location?.length || 0) + (currentFilters.gender?.length || 0) +
            (currentFilters.status?.length || 0) + (currentFilters.activity ? 1 : 0) +
            (currentFilters.cvLanguage?.length || 0) + (currentFilters.hasPhoto ? 1 : 0) +
            (currentFilters.hasRecommendation ? 1 : 0) + (currentFilters.age ? 1 : 0);
          return (
            <div className="filter-section">
              <button className="filter-section-header" onClick={() => openFilterModal('personalData')}>
                <div className="header-left">
                  <span className="section-icon-wrap">
                    <User size={18} />
                    {count > 0 && <span className="filter-badge">{count}</span>}
                  </span>
                  <span>ASMENINIAI DUOMENYS</span>
                </div>
                <span className="plus-icon">+</span>
              </button>
            </div>
          );
        })()}

        {/* Work Experience */}
        {(() => {
          const count = (currentFilters.experience?.length || 0) + (currentFilters.lastWorkField?.length || 0) +
            (currentFilters.companyNames?.length || 0) + (currentFilters.companyActivity?.length || 0);
          return (
            <div className="filter-section">
              <button className="filter-section-header" onClick={() => openFilterModal('experience')}>
                <div className="header-left">
                  <span className="section-icon-wrap">
                    <Briefcase size={18} />
                    {count > 0 && <span className="filter-badge">{count}</span>}
                  </span>
                  <span>DARBO PATIRTIS</span>
                </div>
                <span className="plus-icon">+</span>
              </button>
            </div>
          );
        })()}

        {/* Education */}
        {(() => {
          const count = currentFilters.education?.length || 0;
          return (
            <div className="filter-section">
              <button className="filter-section-header" onClick={() => openFilterModal('education')}>
                <div className="header-left">
                  <span className="section-icon-wrap">
                    <GraduationCap size={18} />
                    {count > 0 && <span className="filter-badge">{count}</span>}
                  </span>
                  <span>IŠSILAVINIMAS</span>
                </div>
                <span className="plus-icon">+</span>
              </button>
            </div>
          );
        })()}

        {/* Language Skills */}
        {(() => {
          const count = currentFilters.languages?.length || 0;
          return (
            <div className="filter-section">
              <button className="filter-section-header" onClick={() => openFilterModal('languages')}>
                <div className="header-left">
                  <span className="section-icon-wrap">
                    <Globe size={18} />
                    {count > 0 && <span className="filter-badge">{count}</span>}
                  </span>
                  <span>KALBŲ ĮGŪDŽIAI</span>
                </div>
                <span className="plus-icon">+</span>
              </button>
            </div>
          );
        })()}

        {/* Computer Skills */}
        {(() => {
          const count = currentFilters.computerSkills?.length || 0;
          return (
            <div className="filter-section">
              <button className="filter-section-header" onClick={() => openFilterModal('computerSkills')}>
                <div className="header-left">
                  <span className="section-icon-wrap">
                    <Monitor size={18} />
                    {count > 0 && <span className="filter-badge">{count}</span>}
                  </span>
                  <span>DARBO KOMPIUTERIU ĮGŪDŽIAI</span>
                </div>
                <span className="plus-icon">+</span>
              </button>
            </div>
          );
        })()}

        {/* Driving Skills */}
        {(() => {
          const count = currentFilters.drivingLicense?.length || 0;
          return (
            <div className="filter-section">
              <button className="filter-section-header" onClick={() => openFilterModal('drivingSkills')}>
                <div className="header-left">
                  <span className="section-icon-wrap">
                    <Car size={18} />
                    {count > 0 && <span className="filter-badge">{count}</span>}
                  </span>
                  <span>VAIRAVIMO ĮGŪDŽIAI</span>
                </div>
                <span className="plus-icon">+</span>
              </button>
            </div>
          );
        })()}

        {/* Requirements for Future Job */}
        {(() => {
          const count = (currentFilters.workType?.length || 0) + (currentFilters.salary ? 1 : 0);
          return (
            <div className="filter-section">
              <button className="filter-section-header" onClick={() => openFilterModal('requirements')}>
                <div className="header-left">
                  <span className="section-icon-wrap">
                    <Target size={18} />
                    {count > 0 && <span className="filter-badge">{count}</span>}
                  </span>
                  <span>REIKALAVIMAI BŪSIMAM DARBUI</span>
                </div>
                <span className="plus-icon">+</span>
              </button>
            </div>
          );
        })()}
      </div>

      {/* Filter Modals */}
      {openModal === 'personalData' && (
        <FilterModal
          title="Asmeniniai duomenys"
          icon={<User size={20} />}
          isOpen={true}
          onClose={closeModal}
          resultCount={liveCount}
          countLoading={countLoading}
          facetsLoading={facetsLoading}
          countLabel="candidates"
          onClearAll={() => {
            setLocalAgeMin(16);
            setLocalAgeMax(65);
            const cleared = { ...localFilters, gender: [], status: [], activity: '', cvLanguage: [], hasPhoto: false, hasRecommendation: false, acceptsGigs: false, location: [] };
            setLocalFilters(cleared);
            triggerPreview(cleared, 16, 65);
          }}
        >
          <div className="two-column-grid">
            {/* Left Column - Personal filters */}
            <div className="filter-column">
              {/* Amžius */}
              <div className="modal-filter-group">
                <h4>Amžius</h4>
                <div className="age-slider-container">
                  <div className="age-slider-track">
                    <div 
                      className="age-slider-progress"
                      style={{
                        left: `${((localAgeMin - 16) / 49) * 100}%`,
                        right: `${100 - ((localAgeMax - 16) / 49) * 100}%`
                      }}
                    />
                    <input
                      type="range"
                      min="16"
                      max="65"
                      value={localAgeMin}
                      onChange={(e) => {
                        const newMin = parseInt(e.target.value);
                        if (newMin < localAgeMax) {
                          setLocalAgeMin(newMin);
                        }
                      }}
                      className="age-slider age-slider-min"
                    />
                    <input
                      type="range"
                      min="16"
                      max="65"
                      value={localAgeMax}
                      onChange={(e) => {
                        const newMax = parseInt(e.target.value);
                        if (newMax > localAgeMin) {
                          setLocalAgeMax(newMax);
                        }
                      }}
                      className="age-slider age-slider-max"
                    />
                  </div>
                  <div className="age-values">
                    <span>{localAgeMin}</span>
                    <span>{localAgeMax}</span>
                  </div>
                </div>
              </div>

              {/* Lytis */}
              <div className="modal-filter-group">
                <h4>Lytis</h4>
                <div className="checkbox-row">
                  {['Vyras', 'Moteris'].map(val => {
                    const cnt = facets.gender?.[val] ?? 0;
                    const disabled = facetsReady && cnt === 0;
                    return (
                      <label key={val} className="checkbox-item" style={{ opacity: disabled ? 0.4 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
                        <input type="checkbox" checked={localFilters.gender?.includes(val) || false} onChange={() => handleLocalArrayCheckbox('gender', val)} />
                        <span>{val}{facetsReady && cnt > 0 && <span className="filter-option-count"> ({cnt})</span>}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Statusas */}
              <div className="modal-filter-group">
                <h4>Statusas</h4>
                <div className="checkbox-list single-column">
                  <label className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={localFilters.status?.includes('currently_employed') || false}
                      onChange={() => handleLocalArrayCheckbox('status', 'currently_employed')}
                    />
                    <span>Šiuo metu dirbantis</span>
                  </label>
                </div>
              </div>

              {/* Aktyvumas */}
              <div className="modal-filter-group">
                <h4>Aktyvumas</h4>
                <p className="filter-subtitle">(Paskutinio prisijungimo prie svetainės laikas)</p>
                <div className="checkbox-list single-column">
                  {['Per paskutinę savaitę', 'Per paskutines 2 savaites', 'Per paskutinį mėnesį', 'Per paskutinius 3 mėnesius'].map((activity) => {
                    const cnt = facets.activity?.[activity] ?? 0;
                    const selected = localFilters.activity === activity;
                    const disabled = facetsReady && cnt === 0 && !selected;
                    return (
                      <label key={activity} className="checkbox-item" style={{ opacity: disabled ? 0.4 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
                        <input
                          type="radio"
                          name="activity"
                          checked={selected}
                          onChange={() => handleLocalInputChange('activity', activity)}
                          onClick={() => {
                            if (localFilters.activity === activity) {
                              handleLocalInputChange('activity', '');
                            }
                          }}
                        />
                        <span>{activity}{facetsReady && cnt > 0 && <span className="filter-option-count"> ({cnt})</span>}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* CV kalba */}
              <div className="modal-filter-group">
                <h4>CV kalba</h4>
                <div className="checkbox-list single-column">
                  {(cvTemplates.cv_language || []).map((lang) => {
                    const cnt = facets.cvLanguage?.[lang.value] ?? 0;
                    const disabled = facetsReady && cnt === 0;
                    return (
                      <label key={lang.id} className="checkbox-item" style={{ opacity: disabled ? 0.4 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
                        <input type="checkbox" checked={localFilters.cvLanguage?.includes(lang.value) || false} onChange={() => handleLocalArrayCheckbox('cvLanguage', lang.value)} />
                        <span>{lang.value}{facetsReady && cnt > 0 && <span className="filter-option-count"> ({cnt})</span>}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Nuotrauka & Rekomendacija */}
              <div className="modal-filter-group">
                <h4>Nuotrauka</h4>
                <div className="checkbox-list single-column">
                  {(() => {
                    const cnt = facets.hasPhoto?.['true'] ?? 0;
                    const checked = localFilters.hasPhoto || false;
                    const disabled = facetsReady && cnt === 0 && !checked;
                    return (
                      <label className="checkbox-item" style={{ opacity: disabled ? 0.4 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => handleLocalBooleanChange('hasPhoto', !localFilters.hasPhoto)}
                        />
                        <span>CV su nuotrauka{facetsReady && cnt > 0 && <span className="filter-option-count"> ({cnt})</span>}</span>
                      </label>
                    );
                  })()}
                </div>
              </div>

              <div className="modal-filter-group">
                <h4>Rekomendacija</h4>
                <div className="checkbox-list single-column">
                  {(() => {
                    const cnt = facets.hasRecommendation?.['true'] ?? 0;
                    const checked = localFilters.hasRecommendation || false;
                    const disabled = facetsReady && cnt === 0 && !checked;
                    return (
                      <label className="checkbox-item" style={{ opacity: disabled ? 0.4 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => handleLocalBooleanChange('hasRecommendation', !localFilters.hasRecommendation)}
                        />
                        <span>CV su rekomendacija{facetsReady && cnt > 0 && <span className="filter-option-count"> ({cnt})</span>}</span>
                      </label>
                    );
                  })()}
                </div>
              </div>

              <div className="modal-filter-group">
                <h4>Freelance</h4>
                <div className="checkbox-list single-column">
                  {(() => {
                    const cnt = facets.acceptsGigs?.['true'] ?? 0;
                    const checked = localFilters.acceptsGigs || false;
                    const disabled = facetsReady && cnt === 0 && !checked;
                    return (
                      <label className="checkbox-item" style={{ opacity: disabled ? 0.4 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => handleLocalBooleanChange('acceptsGigs', !localFilters.acceptsGigs)}
                        />
                        <span>Priima Freelance pasiūlymus{facetsReady && cnt > 0 && <span className="filter-option-count"> ({cnt})</span>}</span>
                      </label>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Right Column - Gyvenamoji vieta */}
            <div className="filter-column">
              <h4>Gyvenamoji vieta</h4>
              <input
                type="text"
                placeholder="Įveskite miestą"
                value={locationSearch}
                onChange={(e) => setLocationSearch(e.target.value)}
                className="filter-search-input"
              />
              <div className="checkbox-list scrollable">
                {pinMajorCitiesFirst(
                  cvTemplates.city || LITHUANIAN_CITIES.map((c, i) => ({ id: i, category: 'city', value: c })),
                )
                  .filter(city => !locationSearch || city.value.toLowerCase().includes(locationSearch.toLowerCase()))
                  .map((city) => {
                    const cnt = facets.location?.[city.value] ?? 0;
                    const disabled = facetsReady && cnt === 0;
                    return (
                      <label key={city.id || city.value} className="checkbox-item" style={{ opacity: disabled ? 0.4 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
                        <input type="checkbox" checked={localFilters.location.includes(city.value)} onChange={() => handleLocalArrayCheckbox('location', city.value)} />
                        <span>{city.value}{facetsReady && cnt > 0 && <span className="filter-option-count"> ({cnt})</span>}</span>
                      </label>
                    );
                  })}
              </div>
            </div>
          </div>
        </FilterModal>
      )}

      {openModal === 'experience' && (
        <FilterModal
          title="Darbo patirtis"
          icon={<Briefcase size={20} />}
          isOpen={true}
          onClose={closeModal}
          resultCount={liveCount}
          countLoading={countLoading}
          facetsLoading={facetsLoading}
          countLabel="candidates"
          onClearAll={() => {
            const cleared = { ...localFilters, experience: [], lastWorkField: [], companyNames: [], companyActivity: [], positionExperienceYears: {} };
            setLocalFilters(cleared);
            triggerPreview(cleared);
          }}
        >
          {/* Tab Navigation */}
          <div className="experience-tabs">
            <button 
              className={`tab-btn ${experienceTab === 'workField' ? 'active' : ''}`}
              onClick={() => setExperienceTab('workField')}
            >
              Darbo patirtis
            </button>
            <button 
              className={`tab-btn ${experienceTab === 'lastWorkField' ? 'active' : ''}`}
              onClick={() => setExperienceTab('lastWorkField')}
            >
              Paskutinė darbo patirtis
            </button>
            <button 
              className={`tab-btn ${experienceTab === 'companies' ? 'active' : ''}`}
              onClick={() => setExperienceTab('companies')}
            >
              Darbo patirtis - įmonės
            </button>
          </div>

          {/* Tab Content */}
          {experienceTab === 'workField' && (
            <div className="two-column-layout">
              {/* Left Column - Work Fields */}
              <div className="filter-column">
                <h4>Darbo sritys</h4>
                <input
                  type="text"
                  placeholder="Įveskite darbo sritį"
                  value={workFieldSearch}
                  onChange={(e) => setWorkFieldSearch(e.target.value)}
                  className="filter-search-input"
                />
                <div className="checkbox-list scrollable">
                  {(cvTemplates.work_field || [])
                    .filter(wf => !workFieldSearch || wf.value.toLowerCase().includes(workFieldSearch.toLowerCase()))
                    .map((workField) => {
                      const checked = localFilters.experience.includes(workField.value);
                      const count = facets.workFields?.[workField.value] ?? 0;
                      const disabled = facetsReady && count === 0 && !checked;
                      return (
                        <label key={workField.id} className="checkbox-item" style={{ opacity: disabled ? 0.4 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => handleLocalArrayCheckbox('experience', workField.value)}
                          />
                          <span>{workField.value}{facetsReady && count > 0 && <span className="filter-option-count"> ({count})</span>}</span>
                        </label>
                      );
                    })}
                </div>
              </div>
              
              {/* Right Column - Position Types + Positions */}
              <div className="filter-column">
                <h4>Pareigybių tipai</h4>
                <div className="checkbox-list">
                  {(cvTemplates.position_type || []).map((posType) => {
                    const checked = localFilters.experience.includes(posType.value);
                    const count = facets.positionType?.[posType.value] ?? 0;
                    const disabled = facetsReady && count === 0 && !checked;
                    return (
                    <label key={posType.id} className="checkbox-item" style={{ opacity: disabled ? 0.4 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => handleLocalArrayCheckbox('experience', posType.value)}
                      />
                      <span>{posType.value}{facetsReady && count > 0 && <span className="filter-option-count"> ({count})</span>}</span>
                    </label>
                    );
                  })}
                </div>

                <div style={{ marginTop: '24px' }}>
                  <h4>Pareigybės pagal pasirinktas darbo sritis</h4>
                  {localFilters.experience.filter(e => (cvTemplates.work_field || []).some(wf => wf.value === e)).length === 0 ? (
                    <div className="no-results-hint">
                      Pasirinkite bent vieną darbo sritį
                    </div>
                  ) : (
                    <>
                      <input
                        type="text"
                        placeholder="Įveskite pareigybę"
                        value={positionSearch}
                        onChange={(e) => setPositionSearch(e.target.value)}
                        className="filter-search-input"
                      />
                      <div className="checkbox-list scrollable">
                        {(() => {
                          const selectedWorkFields = localFilters.experience.filter(e =>
                            (cvTemplates.work_field || []).some(wf => wf.value === e)
                          );
                          const filtered = (cvTemplates.position || [])
                            .filter(pos =>
                              (pos.parent_value || '').split(',').map(v => v.trim()).some(pf => selectedWorkFields.includes(pf))
                            )
                            .filter(pos => !positionSearch || pos.value.toLowerCase().includes(positionSearch.toLowerCase()));
                          if (filtered.length === 0) {
                            return (
                              <div className="no-results-hint">
                                {positionSearch ? 'Pagal paiešką pareigybių nerasta' : 'Pasirinktose darbo srityse pareigybių nerasta'}
                              </div>
                            );
                          }
                          return filtered.map((pos) => renderPositionRow(pos));
                        })()}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {experienceTab === 'lastWorkField' && (
            <div className="two-column-layout">
              {/* Left Column - Last Work Fields */}
              <div className="filter-column">
                <h4>Darbo sritys</h4>
                <input
                  type="text"
                  placeholder="Įveskite darbo sritį"
                  value={workFieldSearch}
                  onChange={(e) => setWorkFieldSearch(e.target.value)}
                  className="filter-search-input"
                />
                <div className="checkbox-list scrollable two-columns">
                  {(cvTemplates.work_field || [])
                    .filter(wf => !workFieldSearch || wf.value.toLowerCase().includes(workFieldSearch.toLowerCase()))
                    .map((workField) => {
                      const checked = (localFilters.lastWorkField || []).includes(workField.value);
                      const count = facets.lastWorkField?.[workField.value] ?? 0;
                      const disabled = facetsReady && count === 0 && !checked;
                      return (
                        <label key={workField.id} className="checkbox-item" style={{ opacity: disabled ? 0.4 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => handleLocalArrayCheckbox('lastWorkField', workField.value)}
                          />
                          <span>{workField.value}{facetsReady && count > 0 && <span className="filter-option-count"> ({count})</span>}</span>
                        </label>
                      );
                    })}
                </div>
              </div>
              
              {/* Right Column - Position Types + Positions */}
              <div className="filter-column">
                <h4>Pareigybių tipai</h4>
                <div className="checkbox-list">
                  {(cvTemplates.position_type || []).map((posType) => {
                    const checked = localFilters.experience.includes(posType.value);
                    const count = facets.positionType?.[posType.value] ?? 0;
                    const disabled = facetsReady && count === 0 && !checked;
                    return (
                    <label key={posType.id} className="checkbox-item" style={{ opacity: disabled ? 0.4 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => handleLocalArrayCheckbox('experience', posType.value)}
                      />
                      <span>{posType.value}{facetsReady && count > 0 && <span className="filter-option-count"> ({count})</span>}</span>
                    </label>
                    );
                  })}
                </div>

                <div style={{ marginTop: '24px' }}>
                  <h4>Pareigybės pagal pasirinktas darbo sritis</h4>
                  {(localFilters.lastWorkField || []).length === 0 ? (
                    <div className="no-results-hint">
                      Pasirinkite bent vieną darbo sritį
                    </div>
                  ) : (
                    <>
                      <input
                        type="text"
                        placeholder="Įveskite pareigybę"
                        value={positionSearch}
                        onChange={(e) => setPositionSearch(e.target.value)}
                        className="filter-search-input"
                      />
                      <div className="checkbox-list scrollable">
                        {(() => {
                          const filtered = (cvTemplates.position || [])
                            .filter(pos =>
                              (pos.parent_value || '').split(',').map(v => v.trim()).some(pf => (localFilters.lastWorkField || []).includes(pf))
                            )
                            .filter(pos => !positionSearch || pos.value.toLowerCase().includes(positionSearch.toLowerCase()));
                          if (filtered.length === 0) {
                            return (
                              <div className="no-results-hint">
                                {positionSearch ? 'Pagal paiešką pareigybių nerasta' : 'Pasirinktose darbo srityse pareigybių nerasta'}
                              </div>
                            );
                          }
                          return filtered.map((pos) => renderPositionRow(pos));
                        })()}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {experienceTab === 'companies' && (
            <div className="two-column-layout">
              {/* Left Column - Company Activity Sectors */}
              <div className="filter-column">
                <h4>Įmonių veiklos sritys</h4>
                <div className="checkbox-list scrollable">
                  {(cvTemplates.work_field || []).map((field) => {
                    const checked = (localFilters.companyActivity || []).includes(field.value);
                    const count = facets.companyActivity?.[field.value] ?? 0;
                    const disabled = facetsReady && count === 0 && !checked;
                    return (
                    <label key={field.id} className="checkbox-item" style={{ opacity: disabled ? 0.4 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => handleLocalArrayCheckbox('companyActivity', field.value)}
                      />
                      <span>{field.value}{facetsReady && count > 0 && <span className="filter-option-count"> ({count})</span>}</span>
                    </label>
                    );
                  })}
                </div>
              </div>
              
              {/* Right Column - Companies (filtered by selected sectors) */}
              <div className="filter-column companies-column">
                <h4>Įmonės</h4>
                <input
                  type="text"
                  placeholder="Įveskite įmonę"
                  value={companySearch}
                  onChange={(e) => setCompanySearch(e.target.value)}
                  className="filter-search-input"
                />
                {loadingCompanies ? (
                  <div className="loading-companies">
                    <Loader2 size={20} className="spin" />
                    <span>Kraunama...</span>
                  </div>
                ) : (
                  <div className="checkbox-list scrollable two-columns">
                    {(() => {
                      const selectedSectors = localFilters.companyActivity || [];
                      // Count = how many candidates in the current result set list this
                      // company in their work experience (contextual facet), so it can
                      // never exceed the number of matching candidates.
                      const filtered = companies
                        .filter(c => {
                          if (selectedSectors.length > 0 && !selectedSectors.includes(c.activitySector || '')) {
                            return false;
                          }
                          if (companySearch && !c.companyName.toLowerCase().includes(companySearch.toLowerCase())) {
                            return false;
                          }
                          return true;
                        })
                        // Surface companies that actually have matching candidates first.
                        .sort((a, b) => {
                          const ca = facets.companyNames?.[a.companyName] ?? 0;
                          const cb = facets.companyNames?.[b.companyName] ?? 0;
                          if (cb !== ca) return cb - ca;
                          return a.companyName.localeCompare(b.companyName);
                        });
                      
                      if (selectedSectors.length > 0 && filtered.length === 0) {
                        return (
                          <div className="no-results-hint">
                            Pasirinktose veiklos srityse įmonių nerasta
                          </div>
                        );
                      }
                      
                      return filtered.map((company, idx) => {
                        const count = facets.companyNames?.[company.companyName] ?? 0;
                        const checked = (localFilters.companyNames || []).includes(company.companyName);
                        const disabled = facetsReady && count === 0 && !checked;
                        return (
                          <label key={idx} className="checkbox-item" style={{ opacity: disabled ? 0.4 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => handleLocalArrayCheckbox('companyNames', company.companyName)}
                            />
                            <span>{company.companyName}{facetsReady && count > 0 && <span className="count"> ({count})</span>}</span>
                          </label>
                        );
                      });
                    })()}
                  </div>
                )}
              </div>
            </div>
          )}
        </FilterModal>
      )}

      {openModal === 'education' && (
        <FilterModal
          title="Išsilavinimas"
          icon={<GraduationCap size={20} />}
          isOpen={true}
          onClose={closeModal}
          resultCount={liveCount}
          countLoading={countLoading}
          facetsLoading={facetsLoading}
          countLabel="candidates"
          onClearAll={() => { const c = { ...localFilters, education: [] }; setLocalFilters(c); triggerPreview(c); }}
        >
          <div className="modal-filter-group">
            <div className="checkbox-list">
              {(cvTemplates.education_level || []).map((edu) => {
                const cnt = facets.education?.[edu.value] ?? 0;
                const disabled = facetsReady && cnt === 0;
                return (
                  <label key={edu.id} className="checkbox-item" style={{ opacity: disabled ? 0.4 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
                    <input type="checkbox" checked={localFilters.education.includes(edu.value)} onChange={() => handleLocalArrayCheckbox('education', edu.value)} />
                    <span>{edu.value}{facetsReady && cnt > 0 && <span className="filter-option-count"> ({cnt})</span>}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </FilterModal>
      )}

      {openModal === 'languages' && (
        <FilterModal
          title="Kalbų įgūdžiai"
          icon={<Globe size={20} />}
          isOpen={true}
          onClose={closeModal}
          resultCount={liveCount}
          countLoading={countLoading}
          facetsLoading={facetsLoading}
          countLabel="candidates"
          onClearAll={() => { const c = { ...localFilters, languages: [] }; setLocalFilters(c); triggerPreview(c); }}
        >
          <div className="modal-filter-group">
            <div className="checkbox-list">
              {(cvTemplates.language || []).map((lang) => {
                const cnt = facets.languages?.[lang.value] ?? 0;
                const disabled = facetsReady && cnt === 0;
                return (
                  <label key={lang.id} className="checkbox-item" style={{ opacity: disabled ? 0.4 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
                    <input type="checkbox" checked={localFilters.languages.includes(lang.value)} onChange={() => handleLocalArrayCheckbox('languages', lang.value)} />
                    <span>{lang.value}{facetsReady && cnt > 0 && <span className="filter-option-count"> ({cnt})</span>}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </FilterModal>
      )}

      {openModal === 'computerSkills' && (
        <FilterModal
          title="Darbo kompiuteriu įgūdžiai"
          icon={<Monitor size={20} />}
          isOpen={true}
          onClose={closeModal}
          resultCount={liveCount}
          countLoading={countLoading}
          facetsLoading={facetsLoading}
          countLabel="candidates"
          onClearAll={() => { const c = { ...localFilters, computerSkills: [] }; setLocalFilters(c); triggerPreview(c); }}
        >
          {([
            { key: 'computer_skill_office', label: 'Biuro programos' },
            { key: 'computer_skill_accounting', label: 'Apskaitos programos' },
            { key: 'computer_skill_os', label: 'Operacinės sistemos' },
            { key: 'computer_skill_programming', label: 'Programavimo kalbos' },
            { key: 'computer_skill_database', label: 'Duomenų bazės' },
            { key: 'computer_skill_design', label: 'Dizaino programos' },
          ] as { key: keyof typeof cvTemplates; label: string }[]).map(({ key, label }) =>
            (cvTemplates[key] as CVTemplate[] || []).length > 0 && (
              <div key={key} className="modal-filter-group">
                <label>{label}</label>
                <div className="checkbox-list">
                  {(cvTemplates[key] as CVTemplate[]).map((skill) => {
                    const cnt = facets.computerSkills?.[skill.value] ?? 0;
                    const disabled = facetsReady && cnt === 0;
                    return (
                      <label key={skill.id} className="checkbox-item" style={{ opacity: disabled ? 0.4 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
                        <input type="checkbox" checked={localFilters.computerSkills.includes(skill.value)} onChange={() => handleLocalArrayCheckbox('computerSkills', skill.value)} />
                        <span>{skill.value}{facetsReady && cnt > 0 && <span className="filter-option-count"> ({cnt})</span>}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )
          )}
        </FilterModal>
      )}

      {openModal === 'drivingSkills' && (
        <FilterModal
          title="Vairavimo įgūdžiai"
          icon={<Car size={20} />}
          isOpen={true}
          onClose={closeModal}
          resultCount={liveCount}
          countLoading={countLoading}
          facetsLoading={facetsLoading}
          countLabel="candidates"
          onClearAll={() => { const c = { ...localFilters, drivingLicense: [] }; setLocalFilters(c); triggerPreview(c); }}
        >
          <div className="modal-filter-group">
            <div className="checkbox-list">
              {(cvTemplates.driving_category?.length
                ? cvTemplates.driving_category
                : ['AM', 'A1', 'A2', 'A', 'B', 'BE', 'C', 'CE', 'D', 'DE', 'E'].map((v, i) => ({ id: i, category: 'driving_category', value: v }))
              ).map((license) => {
                const cnt = facets.drivingLicense?.[license.value] ?? 0;
                const disabled = facetsReady && cnt === 0;
                return (
                  <label key={license.id} className="checkbox-item" style={{ opacity: disabled ? 0.4 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
                    <input type="checkbox" checked={localFilters.drivingLicense.includes(license.value)} onChange={() => handleLocalArrayCheckbox('drivingLicense', license.value)} />
                    <span>{license.value}{facetsReady && cnt > 0 && <span className="filter-option-count"> ({cnt})</span>}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </FilterModal>
      )}

      {openModal === 'requirements' && (
        <FilterModal
          title="Reikalavimai būsimam darbuotojui"
          icon={<Target size={20} />}
          isOpen={true}
          onClose={closeModal}
          resultCount={liveCount}
          countLoading={countLoading}
          facetsLoading={facetsLoading}
          countLabel="candidates"
          onClearAll={() => { const c = { ...localFilters, workType: [], desiredWorkField: [], desiredPositionType: [], desiredWorkTime: [], desiredPosition: [], desiredLocation: [], salary: '' }; setLocalFilters(c); triggerPreview(c); }}
        >
          {/* 1. Darbo vieta */}
          <div className="modal-filter-group">
            <label>Darbo vieta (pageidaujama)</label>
            <input
              type="text"
              placeholder="Įveskite miestą"
              value={desiredLocationSearch}
              onChange={(e) => setDesiredLocationSearch(e.target.value)}
              className="filter-search-input"
            />
            <div className="checkbox-list scrollable">
              {pinMajorCitiesFirst(
                cvTemplates.city || LITHUANIAN_CITIES.map((c, i) => ({ id: i, category: 'city', value: c })),
              )
                .filter(city => !desiredLocationSearch || city.value.toLowerCase().includes(desiredLocationSearch.toLowerCase()))
                .map((city) => {
                  const cnt = facets.desiredLocation?.[city.value] ?? 0;
                  const checked = localFilters.desiredLocation?.includes(city.value) || false;
                  const disabled = facetsReady && cnt === 0 && !checked;
                  return (
                    <label key={city.id || city.value} className="checkbox-item" style={{ opacity: disabled ? 0.4 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => handleLocalArrayCheckbox('desiredLocation', city.value)}
                      />
                      <span>{city.value}{facetsReady && cnt > 0 && <span className="filter-option-count"> ({cnt})</span>}</span>
                    </label>
                  );
                })}
            </div>
          </div>

          {/* 2. Darbo tipas */}
          <div className="modal-filter-group">
            <label>Darbo tipas</label>
            <div className="checkbox-list">
              {orderByPriority(cvTemplates.work_type || [], WORK_TYPE_ORDER).map((type) => {
                const cnt = facets.workType?.[type.value] ?? 0;
                const disabled = facetsReady && cnt === 0;
                return (
                  <label key={type.id} className="checkbox-item" style={{ opacity: disabled ? 0.4 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
                    <input type="checkbox" checked={localFilters.workType.includes(type.value)} onChange={() => handleLocalArrayCheckbox('workType', type.value)} />
                    <span>{type.value}{facetsReady && cnt > 0 && <span className="filter-option-count"> ({cnt})</span>}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* 3. Darbo laikas */}
          <div className="modal-filter-group">
            <label>Darbo laikas (pageidaujama)</label>
            <div className="checkbox-list">
              {orderByPriority(cvTemplates.work_time || [], WORK_TIME_ORDER).map((wt) => {
                const cnt = facets.desiredWorkTime?.[wt.value] ?? 0;
                const checked = localFilters.desiredWorkTime?.includes(wt.value) || false;
                const disabled = facetsReady && cnt === 0 && !checked;
                return (
                  <label key={wt.id || wt.value} className="checkbox-item" style={{ opacity: disabled ? 0.4 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleLocalArrayCheckbox('desiredWorkTime', wt.value)}
                    />
                    <span>{wt.value}{facetsReady && cnt > 0 && <span className="filter-option-count"> ({cnt})</span>}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* 4. Darbo sritis */}
          <div className="modal-filter-group">
            <label>Darbo sritis (pageidaujama)</label>
            <div className="checkbox-list scrollable">
              {(cvTemplates.work_field || []).map((wf) => {
                const cnt = facets.desiredWorkField?.[wf.value] ?? 0;
                const checked = localFilters.desiredWorkField?.includes(wf.value) || false;
                const disabled = facetsReady && cnt === 0 && !checked;
                return (
                  <label key={wf.id || wf.value} className="checkbox-item" style={{ opacity: disabled ? 0.4 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleLocalArrayCheckbox('desiredWorkField', wf.value)}
                    />
                    <span>{wf.value}{facetsReady && cnt > 0 && <span className="filter-option-count"> ({cnt})</span>}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* 5. Pareigybių tipas */}
          <div className="modal-filter-group">
            <label>Pareigybių tipas (pageidaujama)</label>
            <div className="checkbox-list">
              {(cvTemplates.position_type || []).map((pt) => {
                const cnt = facets.desiredPositionType?.[pt.value] ?? 0;
                const checked = localFilters.desiredPositionType?.includes(pt.value) || false;
                const disabled = facetsReady && cnt === 0 && !checked;
                return (
                  <label key={pt.id || pt.value} className="checkbox-item" style={{ opacity: disabled ? 0.4 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleLocalArrayCheckbox('desiredPositionType', pt.value)}
                    />
                    <span>{pt.value}{facetsReady && cnt > 0 && <span className="filter-option-count"> ({cnt})</span>}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* 6. Pareigybė — only after a work field is chosen (don't expand the full list) */}
          <div className="modal-filter-group">
            <label>Pareigybė (pageidaujama)</label>
            {(!localFilters.desiredWorkField || localFilters.desiredWorkField.length === 0) ? (
              <div className="no-results-hint">Pirmiausia pasirinkite darbo sritį</div>
            ) : (
              <>
                <input
                  type="text"
                  placeholder="Įveskite pareigybę"
                  value={desiredPositionSearch}
                  onChange={(e) => setDesiredPositionSearch(e.target.value)}
                  className="filter-search-input"
                />
                <div className="checkbox-list scrollable">
                  {(() => {
                    const filtered = (cvTemplates.position || [])
                      .filter(pos =>
                        (pos.parent_value || '').split(',').map(v => v.trim()).some(pf => (localFilters.desiredWorkField || []).includes(pf))
                      )
                      .filter(pos => !desiredPositionSearch || pos.value.toLowerCase().includes(desiredPositionSearch.toLowerCase()));
                    if (filtered.length === 0) {
                      return (
                        <div className="no-results-hint">
                          {desiredPositionSearch ? 'Pagal paiešką pareigybių nerasta' : 'Pasirinktose darbo srityse pareigybių nerasta'}
                        </div>
                      );
                    }
                    return filtered.map((pos) => {
                      const cnt = facets.desiredPosition?.[pos.value] ?? 0;
                      const checked = localFilters.desiredPosition?.includes(pos.value) || false;
                      const disabled = facetsReady && cnt === 0 && !checked;
                      return (
                        <label key={pos.id || pos.value} className="checkbox-item" style={{ opacity: disabled ? 0.4 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => handleLocalArrayCheckbox('desiredPosition', pos.value)}
                          />
                          <span>{pos.value}{facetsReady && cnt > 0 && <span className="filter-option-count"> ({cnt})</span>}</span>
                        </label>
                      );
                    });
                  })()}
                </div>
              </>
            )}
          </div>

          {/* 7. Pageidaujamas atlyginimas */}
          <div className="modal-filter-group salary-filter-group">
            <label>Pageidaujamas atlyginimas nuo (netto) EUR</label>
            <div className="salary-input-wrapper">
              <input
                type="number"
                min="0"
                step="100"
                placeholder="Pvz.: 1000"
                value={localFilters.salary}
                onChange={(e) => handleLocalInputChange('salary', e.target.value)}
                className="salary-input"
              />
              <span className="salary-unit">EUR</span>
            </div>
          </div>
        </FilterModal>
      )}

      {/* Save Search Modal */}
      {showSaveModal && (
        <div className="custom-modal-overlay" onClick={cancelSaveSearch}>
          <div className="custom-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="custom-modal-header">
              <h3>Išsaugoti paiešką</h3>
              <button className="custom-modal-close" onClick={cancelSaveSearch}>
                <X size={24} />
              </button>
            </div>
            <div className="custom-modal-body">
              <p>Ar tikrai norite išsaugoti šią paiešką?</p>
              <div className="modal-filter-group">
                <label>Paieškos pavadinimas *</label>
                <input
                  type="text"
                  placeholder="Pvz.: Programuotojai Vilniuje"
                  value={searchTitle}
                  onChange={(e) => setSearchTitle(e.target.value)}
                  disabled={isSaving}
                  autoFocus
                />
              </div>
              {saveMessage && (
                <div className={`save-message ${saveMessage.type}`}>
                  {saveMessage.text}
                </div>
              )}
            </div>
            <div className="custom-modal-footer">
              <button 
                className="btn-secondary" 
                onClick={cancelSaveSearch}
                disabled={isSaving}
              >
                Atšaukti
              </button>
              <button 
                className="btn-primary" 
                onClick={confirmSaveSearch}
                disabled={isSaving}
              >
                {isSaving ? 'Saugoma...' : 'Išsaugoti'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CandidateSearchFilters;

